import { useCallback, useEffect, useRef } from 'react';
import { AiPageContextHandle, pushAiPageContext } from './useAiAssistant';

export type AiTableScalar = string | number | boolean | null;

export interface AiTableColumn {
    key: string;
    label: string;
}

export interface AiTableSourceSnapshot {
    columns: AiTableColumn[];
    rows: Array<Record<string, unknown> | unknown[]>;
    totalRows?: number;
}

export interface AiTableSnapshot {
    columns: AiTableColumn[];
    rows: AiTableScalar[][];
    totalRows: number;
    truncated: boolean;
}

export interface AiTableExposureOptions {
    id?: string;
    title?: string;
    source?: string;
    maxRows?: number;
    redactColumns?: string[];
    redactCell?: (value: unknown, column: AiTableColumn, rowIndex: number) => unknown;
}

export interface AiTableRegistrationOptions extends AiTableExposureOptions {
    rowCount?: number;
    columnCount?: number;
    columnLabels?: string[];
    getSnapshot: () => AiTableSourceSnapshot;
}

export interface AiTableMetadata {
    id: string;
    title: string;
    source?: string;
    rowCount: number;
    columnCount: number;
    columnLabels: string[];
    maxRows: number;
    truncated: boolean;
}

export interface RegisteredAiTable {
    metadata: AiTableMetadata;
    getSnapshot: () => AiTableSnapshot;
}

export interface AiTableAdapter {
    readonly id: string;
    update: (options: Partial<AiTableRegistrationOptions>) => void;
    refresh: () => void;
    activate: () => void;
    unregister: () => void;
}

const DEFAULT_MAX_ROWS = 2000;
const HARD_MAX_ROWS = 5000;
const HARD_MAX_COLUMNS = 100;
const MAX_CELL_LENGTH = 10_000;
const REGISTRY_CONTEXT_DEBOUNCE_MS = 250;

interface RegistryEntry {
    id: string;
    options: AiTableRegistrationOptions;
    activeAt: number;
}

const registry = new Map<string, RegistryEntry>();
let activeSequence = 1;
let contextHandle: AiPageContextHandle | null = null;
let contextTimer: ReturnType<typeof setTimeout> | null = null;

function safeId(value: string): string {
    const cleaned = value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_\-\u0590-\u05ff]+/g, '-')
        .replace(/^-+|-+$/g, '');
    return cleaned || 'table';
}

function uniqueId(requested?: string): string {
    const base = safeId(requested || 'table');
    if (!registry.has(base)) return base;
    let suffix = 2;
    while (registry.has(`${base}-${suffix}`)) suffix += 1;
    return `${base}-${suffix}`;
}

function resolveMaxRows(value?: number): number {
    if (!Number.isFinite(value)) return DEFAULT_MAX_ROWS;
    return Math.max(1, Math.min(HARD_MAX_ROWS, Math.floor(Number(value))));
}

function scalarCell(value: unknown): AiTableScalar {
    if (value == null) return null;
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'number') return Number.isFinite(value) ? value : String(value);
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        return value
            .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '')
            .slice(0, MAX_CELL_LENGTH);
    }
    if (typeof value === 'bigint') return value.toString();
    if (typeof value === 'object') {
        const candidate = value as Record<string, unknown>;
        for (const key of ['label', 'name', 'value']) {
            const nested = candidate[key];
            if (nested == null || typeof nested === 'string' || typeof nested === 'number' || typeof nested === 'boolean') {
                if (nested != null) return scalarCell(nested);
            }
        }
    }
    return '';
}

export function normalizeAiTableSnapshot(
    source: AiTableSourceSnapshot,
    options: Pick<AiTableExposureOptions, 'maxRows' | 'redactColumns' | 'redactCell'> = {},
): AiTableSnapshot {
    const rawRows = Array.isArray(source?.rows) ? source.rows : [];
    const inferredColumns = source?.columns?.length
        ? source.columns
        : (!Array.isArray(rawRows[0]) && rawRows[0] ? Object.keys(rawRows[0]).map((key) => ({ key, label: key })) : []);
    const redacted = new Set((options.redactColumns || []).map(String));
    const columns = inferredColumns
        .filter((column) => column && !redacted.has(String(column.key)))
        .slice(0, HARD_MAX_COLUMNS)
        .map((column) => ({
            key: String(column.key),
            label: String(column.label || column.key),
        }));
    const maxRows = resolveMaxRows(options.maxRows);
    const rows = rawRows.slice(0, maxRows).map((row, rowIndex) =>
        columns.map((column, columnIndex) => {
            const sourceColumnIndex = inferredColumns.findIndex(
                (candidate) => String(candidate.key) === column.key,
            );
            const rawValue = Array.isArray(row)
                ? row[sourceColumnIndex >= 0 ? sourceColumnIndex : columnIndex]
                : row?.[column.key];
            if (!options.redactCell) return scalarCell(rawValue);
            try {
                return scalarCell(options.redactCell(rawValue, column, rowIndex));
            } catch {
                return '[REDACTED]';
            }
        }),
    );
    const totalRows = Math.max(Number(source?.totalRows) || 0, rawRows.length);
    return {
        columns,
        rows,
        totalRows,
        truncated: totalRows > rows.length,
    };
}

function metadataFor(entry: RegistryEntry): AiTableMetadata {
    const { options } = entry;
    const maxRows = resolveMaxRows(options.maxRows);
    const rowCount = Math.max(0, Number(options.rowCount) || 0);
    const columnLabels = (options.columnLabels || []).map(String).slice(0, HARD_MAX_COLUMNS);
    const columnCount = Math.max(0, Number(options.columnCount) || columnLabels.length);
    return {
        id: entry.id,
        title: String(options.title || 'טבלה'),
        source: options.source,
        rowCount,
        columnCount,
        columnLabels,
        maxRows,
        truncated: rowCount > maxRows,
    };
}

function activeEntry(): RegistryEntry | undefined {
    let active: RegistryEntry | undefined;
    registry.forEach((entry) => {
        if (!active || entry.activeAt > active.activeAt) active = entry;
    });
    return active;
}

function publishActiveMetadata(): void {
    contextHandle?.dispose();
    contextHandle = null;
    const entry = activeEntry();
    if (!entry) return;
    const metadata = metadataFor(entry);
    contextHandle = pushAiPageContext({
        page: 'table',
        summary: metadata.title,
        data: {
            active_table: {
                id: metadata.id,
                title: metadata.title,
                source: metadata.source,
                row_count: metadata.rowCount,
                column_count: metadata.columnCount,
                columns: metadata.columnLabels,
                truncated: metadata.truncated,
            },
        },
    }, -10);
}

function scheduleMetadataPublish(): void {
    if (contextTimer) clearTimeout(contextTimer);
    contextTimer = setTimeout(() => {
        contextTimer = null;
        publishActiveMetadata();
    }, REGISTRY_CONTEXT_DEBOUNCE_MS);
}

export function getAiTable(tableId: string): RegisteredAiTable | undefined {
    const entry = registry.get(String(tableId));
    if (!entry) return undefined;
    return {
        metadata: metadataFor(entry),
        getSnapshot: () => normalizeAiTableSnapshot(entry.options.getSnapshot(), entry.options),
    };
}

export function getActiveAiTable(): RegisteredAiTable | undefined {
    const entry = activeEntry();
    return entry ? getAiTable(entry.id) : undefined;
}

export function getActiveAiTableMetadata(): AiTableMetadata | undefined {
    return getActiveAiTable()?.metadata;
}

export function getAiTableSnapshot(tableId?: string): AiTableSnapshot | undefined {
    return (tableId ? getAiTable(tableId) : getActiveAiTable())?.getSnapshot();
}

export function createAiTableAdapter(initialOptions: AiTableRegistrationOptions): AiTableAdapter {
    const id = uniqueId(initialOptions.id || initialOptions.title);
    const entry: RegistryEntry = {
        id,
        options: initialOptions,
        activeAt: activeSequence++,
    };
    registry.set(id, entry);
    scheduleMetadataPublish();
    let registered = true;

    return {
        id,
        update(nextOptions) {
            if (!registered) return;
            entry.options = { ...entry.options, ...nextOptions };
            scheduleMetadataPublish();
        },
        refresh() {
            if (!registered) return;
            scheduleMetadataPublish();
        },
        activate() {
            if (!registered) return;
            entry.activeAt = activeSequence++;
            scheduleMetadataPublish();
        },
        unregister() {
            if (!registered) return;
            registered = false;
            registry.delete(id);
            scheduleMetadataPublish();
        },
    };
}

export function registerAiTable(options: AiTableRegistrationOptions): AiTableAdapter {
    return createAiTableAdapter(options);
}

export function unregisterAiTable(tableId: string): void {
    if (!registry.delete(String(tableId))) return;
    scheduleMetadataPublish();
}

export function useAiTableRegistration(options: AiTableRegistrationOptions | null): {
    tableId?: string;
    refresh: () => void;
    activate: () => void;
} {
    const optionsRef = useRef(options);
    const adapterRef = useRef<AiTableAdapter | null>(null);
    optionsRef.current = options;

    useEffect(() => {
        if (!optionsRef.current) return undefined;
        adapterRef.current = createAiTableAdapter({
            ...optionsRef.current,
            getSnapshot: () => optionsRef.current?.getSnapshot() || { columns: [], rows: [] },
        });
        return () => {
            adapterRef.current?.unregister();
            adapterRef.current = null;
        };
    }, [Boolean(options), options?.id]);

    useEffect(() => {
        if (options) adapterRef.current?.update(options);
    });

    return {
        tableId: adapterRef.current?.id || options?.id,
        refresh: useCallback(() => adapterRef.current?.refresh(), []),
        activate: useCallback(() => adapterRef.current?.activate(), []),
    };
}

/** Extracts visible AG Grid columns and leaf rows in current filter/sort order. */
export function snapshotAgGrid(api: any, columnApi?: any): AiTableSourceSnapshot {
    const displayedColumns = columnApi?.getAllDisplayedColumns?.()
        || api?.getAllDisplayedColumns?.()
        || [];
    const columns: AiTableColumn[] = displayedColumns
        .map((column: any) => {
            const def = column?.getColDef?.() || column?.colDef || {};
            const key = String(column?.getColId?.() || column?.colId || def.field || def.id || '');
            const label = String(def.headerName || def.label || key);
            return key && label ? { key, label } : null;
        })
        .filter(Boolean);
    const rows: Record<string, unknown>[] = [];
    api?.forEachNodeAfterFilterAndSort?.((node: any) => {
        if (!node?.data || node?.group) return;
        const row: Record<string, unknown> = {};
        columns.forEach((column) => {
            const gridColumn = displayedColumns.find((candidate: any) =>
                String(candidate?.getColId?.() || candidate?.colId || '') === column.key);
            row[column.key] = api?.getValue?.(gridColumn || column.key, node) ?? node.data?.[column.key];
        });
        rows.push(row);
    });
    return { columns, rows, totalRows: rows.length };
}
